import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import './Sidebar.css';
import { Fetch } from '../utils/Utils';

// Corral Manager is its own app on its own origin (corral.zapmanejo.com), so
// it can't read this app's localStorage. To open it already logged in, we
// ask the API for a single-use, 60-second handoff code and pass only that
// code; Corral Manager redeems it for its own token (posso-help-api
// internal/handoff). Locally Corral Manager runs on Vite's default port.
const CORRAL_URL = process.env.REACT_APP_CORRAL_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5173' : 'https://corral.zapmanejo.com');

const openCorralManager = () => {
  // Open the tab now, inside the click — browsers block window.open called
  // after the request returns. Drop the opener so Corral Manager can't
  // reach back into this window.
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;
  const go = (url) => {
    if (tab) tab.location.href = url;
    else window.open(url, '_blank', 'noopener');
  };
  Fetch('/api/auth/handoff', 'POST', null,
    (json) => go(`${CORRAL_URL}/?code=${encodeURIComponent(json.code)}`),
    // No code: still open Corral Manager, it shows its own login.
    () => go(CORRAL_URL),
  );
};

const Sidebar = ({
  user,
  ranchName,
  ranches,
  onRanchSwitch,
  tabs,
  activeTab,
  onTabClicked,
  settingsTabs,
  activeSettingsTab,
  onSettingsTabClicked,
  subNavConfig,
  subPaths,
  onSubTabClicked,
  settingsSubNavConfig,
  settingsSubPaths,
  onSettingsSubTabClicked,
  onClickLogo,
  onClickProfile,
  onClickChangePassword,
  hintsVisible,
  onToggleHints,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [expandedTab, setExpandedTab] = useState(null);
  const [expandedSettingsTab, setExpandedSettingsTab] = useState(null);
  const [profileExpanded, setProfileExpanded] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('zapmanejo_token');
    localStorage.removeItem('zapmanejo_user');
    window.location.href = '/';
  };

  // In the mobile drawer (isOpen), tapping a section that has a sub-menu only
  // expands it. Navigating too would load the section's first sub-tab behind
  // the open menu and leave it in history, so Back from the sub-tab the user
  // actually picked would land there. On desktop the sidebar is always
  // visible and a section click still opens its first sub-tab.
  const handleTabClick = (index) => {
    if (!(isOpen && subNavConfig[index])) onTabClicked(index);
    setExpandedSettingsTab(null);
    setProfileExpanded(false);
    if (subNavConfig[index]) {
      setExpandedTab(expandedTab === index ? null : index);
    } else {
      setExpandedTab(null);
      onClose();
    }
  };

  const handleSettingsTabClick = (index) => {
    if (!(isOpen && settingsSubNavConfig[index])) onSettingsTabClicked(index);
    setExpandedTab(null);
    setProfileExpanded(false);
    if (settingsSubNavConfig[index]) {
      setExpandedSettingsTab(expandedSettingsTab === index ? null : index);
    } else {
      setExpandedSettingsTab(null);
      onClose();
    }
  };

  const handleSubTabClick = (mainIndex, subIndex) => {
    onSubTabClicked(mainIndex, subIndex);
    onClose();
  };

  const handleSettingsSubTabClick = (settingsIndex, subIndex) => {
    onSettingsSubTabClicked(settingsIndex, subIndex);
    onClose();
  };

  const avatarLetter = (user?.name || user?.email || 'U')[0].toUpperCase();
  const displayName = user?.name || user?.email || '';

  const getActiveSubTab = (tabIndex) => {
    const slugs = subPaths?.[tabIndex];
    if (!slugs) return -1;
    const basePath = tabs[tabIndex]?.path;
    return slugs.findIndex(slug =>
      location.pathname === `${basePath}/${slug}` ||
      location.pathname.startsWith(`${basePath}/${slug}/`)
    );
  };

  const getActiveSettingsSubTab = (tabIndex) => {
    const slugs = settingsSubPaths?.[tabIndex];
    if (!slugs) return -1;
    const basePath = settingsTabs[tabIndex]?.path;
    return slugs.findIndex(slug =>
      location.pathname === `${basePath}/${slug}` ||
      location.pathname.startsWith(`${basePath}/${slug}/`)
    );
  };

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
        {/* Brand */}
        <button className="sidebar-brand" onClick={() => { onClickLogo?.(); setExpandedTab(null); setExpandedSettingsTab(null); setProfileExpanded(false); onClose(); }}>
          <span className="sidebar-brand-name">ZapManejo</span>
          <span className="sidebar-brand-sub">Gestão Rural</span>
          {ranches && ranches.length > 1 ? (
            <select
              className="sidebar-ranch-switcher"
              value={localStorage.getItem('zapmanejo_ranch_id') || ''}
              onChange={(e) => { e.stopPropagation(); onRanchSwitch?.(e.target.value); }}
              onClick={(e) => e.stopPropagation()}
            >
              {ranches.map(r => (
                <option key={r._id} value={r._id}>{r.name}</option>
              ))}
            </select>
          ) : (
            ranchName && <span className="sidebar-ranch-name">{ranchName}</span>
          )}
        </button>

        {/* Primary nav */}
        <nav className="sidebar-nav">
          {tabs.map((tab, index) => {
            const hasSubNav = !!subNavConfig[index];
            const isActive = activeTab === index;
            const isExpanded = expandedTab === index || isActive;

            return (
              <div key={index}>
                <button
                  className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => handleTabClick(index)}
                >
                  {t(tab.key)}
                  {hasSubNav && (
                    <span className={`sidebar-chevron ${isExpanded ? 'open' : ''}`}>›</span>
                  )}
                </button>
                {hasSubNav && isExpanded && (
                  <div className="sidebar-sub-nav">
                    {subNavConfig[index].map((subKey, subIndex) => {
                      const isSubActive = getActiveSubTab(index) === subIndex;
                      return (
                        <button
                          key={subIndex}
                          className={`sidebar-sub-nav-item ${isSubActive ? 'active' : ''}`}
                          onClick={() => handleSubTabClick(index, subIndex)}
                        >
                          {t(subKey)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <button
            className="sidebar-nav-item"
            data-testid="sidebar-corral-manager"
            onClick={() => { openCorralManager(); onClose(); }}
          >
            {t('corral_manager')}
            <span className="sidebar-external">↗</span>
          </button>
        </nav>

        <div className="sidebar-divider" />

        {/* Settings nav */}
        <nav className="sidebar-nav sidebar-nav-settings">
          {settingsTabs.map((tab, index) => {
            const hasSubNav = !!settingsSubNavConfig[index];
            const isActive = activeSettingsTab === index;
            const isExpanded = expandedSettingsTab === index || isActive;

            return (
              <div key={index}>
                <button
                  className={`sidebar-nav-item sidebar-nav-secondary ${isActive ? 'active' : ''}`}
                  onClick={() => handleSettingsTabClick(index)}
                >
                  {t(tab.key)}
                  {hasSubNav && (
                    <span className={`sidebar-chevron ${isExpanded ? 'open' : ''}`}>›</span>
                  )}
                </button>
                {hasSubNav && isExpanded && (
                  <div className="sidebar-sub-nav">
                    {settingsSubNavConfig[index].map((subKey, subIndex) => {
                      const isSubActive = getActiveSettingsSubTab(index) === subIndex;
                      return (
                        <button
                          key={subIndex}
                          className={`sidebar-sub-nav-item ${isSubActive ? 'active' : ''}`}
                          onClick={() => handleSettingsSubTabClick(index, subIndex)}
                        >
                          {t(subKey)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-spacer" />

        {/* Profile menu */}
        {profileExpanded && (
          <div className="sidebar-sub-nav sidebar-profile-menu">
            <button className="sidebar-sub-nav-item" onClick={() => { onClickProfile(); setProfileExpanded(false); onClose(); }}>
              {t('profile')}
            </button>
            <button className="sidebar-sub-nav-item" onClick={() => { onClickChangePassword(); setProfileExpanded(false); onClose(); }}>
              {t('change_password')}
            </button>
            <button className="sidebar-sub-nav-item" onClick={() => { onToggleHints(); setProfileExpanded(false); onClose(); }}>
              {hintsVisible ? t('hide_hints') : t('show_hints')}
            </button>
            <button className="sidebar-sub-nav-item sidebar-logout" onClick={handleLogout}>
              {t('logout')}
            </button>
          </div>
        )}

        {/* Profile */}
        <button className="sidebar-profile" onClick={() => setProfileExpanded(prev => !prev)}>
          <div className="sidebar-profile-avatar">{avatarLetter}</div>
          <span className="sidebar-profile-name">{displayName}</span>
          <span className={`sidebar-chevron sidebar-profile-chevron ${profileExpanded ? 'open' : ''}`}>›</span>
        </button>
      </aside>
    </>
  );
};

export default Sidebar;
